/**
 * React Providers für Client-Side Funktionalität
 * TanStack Query + Zustand + Auth + MSW (optional)
 */

import React, { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AuthProvider } from '~/shared/lib/auth'
import { initThemeFromLocalStorage } from '~/lib/theme'
import { AppThemeProvider } from '~/providers/AppThemeProvider'

export function Providers({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(false)
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            retry: 1,
          },
        },
      })
  )

  useEffect(() => {
    initThemeFromLocalStorage()

    // Initialize MSW if enabled, then mark ready
    const init = async () => {
      // Mock Service Worker exists for local development only. Guarded by the build-time
      // constant import.meta.env.DEV so the mocks (and their localStorage switch) are not part
      // of the production bundle at all.
      if (import.meta.env.DEV) {
        const { initMocks } = await import('~/mocks')
        await initMocks()
      }
      setIsReady(true)

      document.body.classList.remove('crm-loading')
      window.dispatchEvent(new CustomEvent('crm-app-ready'))
    }
    init()
  }, [])

  // Wait for MSW to initialize before rendering
  if (!isReady) {
    return null
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AppThemeProvider>
        <AuthProvider showLoading={false}>{children}</AuthProvider>
      </AppThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </QueryClientProvider>
  )
}
